/**
 * The engine's labels for the Activity pages (Overview, Devices > activity,
 * Devices > timeline): the screens its mornings labelled, loaded for the
 * days a page shows and turned into the categories time is rolled up by.
 * See util/engineLabels.ts for what a label is and how one is looked up.
 *
 * What's held belongs to one client: a load for another client, or a lock,
 * clears it, and a load that finishes after the client changed (or after a
 * lock, or after a newer load began) is thrown away rather than applied.
 */
import { defineStore } from 'pinia';
import { useHubStore } from '~/stores/hub';
import { useOrgKeyUnlockStore } from '~/stores/orgKeyUnlock';
import { engineCategories, engineColour, engineOptions, engineScore, forgetOpened, loadEngineScreens, openedGeneration, readerFor, shiftDate, } from '~/util/engineLabels';
import { DEFAULT_CLOCK } from '~/util/engineLabels';
export const useEngineLabelsStore = defineStore('engineLabels', {
    state: () => ({
        /** Whose labels these are. */
        orgId: null,
        /** The screens the engine labelled, by the client's day, for the days last loaded. */
        byDay: null,
        /** The days (YYYY-MM-DD) the page shows, as last asked for. */
        shown: [],
        /** The days among those shown that the engine has looked at. */
        days: [],
        /** Days shown the engine looked at whose result has no labels for the
         * page (made by an engine from before it sent them). */
        withoutScreens: [],
        /** The same for the days asked for alongside (the previous period). */
        alsoDays: [],
        /** Why the previous period's labels couldn't be fetched, when they
         * couldn't (the period shown is unaffected). */
        alsoError: null,
        /** Days shown none of whose results would open (wrong key, tampered,
         * offline): their time reads "Couldn't be opened", and the page says so. */
        failed: 0,
        /** Every loaded day none of whose results would open. */
        failedDays: [],
        /** Why the engine's results couldn't be listed at all (the Hub
         * unreachable, or a listing refused), when they couldn't. */
        error: null,
        /** The computers on the staff list each of those days. */
        staffByDay: {},
        /** Each listed computer's Hub id, by day, when the results carry them. */
        idsByDay: {},
        /** The client's clock, from the day results. */
        clock: DEFAULT_CLOCK,
        /** Whether the engine's results can be opened at all (key held, engine chosen and verified). */
        ready: false,
        loading: false,
        /** The load in flight; an older one finishing later is ignored. */
        request: 0,
    }),
    getters: {
        /** The categories for the computers named, or null when the engine has
         * looked at none of the days shown (the page then uses its own list; a
         * result for a neighbouring day alone doesn't switch the page over). */
        classesFor(state) {
            return (devices) => state.byDay && state.days.length
                ? engineCategories(state.byDay, devices, state.clock, state.staffByDay, state.failedDays, state.shown, state.idsByDay)
                : null;
        },
        /** What a page rolls time up by: the engine's labels, and never
         * ActivityWatch's own categories ("Uncategorized", "Work > Programming"),
         * which aren't how Zunox counts work (Shane, 2 Nov). Until the engine has
         * looked at a day, its time reads "Not looked at yet". */
        labelsFor(state) {
            return (devices) => (state.byDay && state.days.length
                ? engineCategories(state.byDay, devices, state.clock, state.staffByDay, state.failedDays, state.shown, state.idsByDay)
                : null) ?? engineCategories({}, devices, state.clock);
        },
        colourFor() {
            return (classes) => engineColour(classes);
        },
        score() {
            return engineScore;
        },
        optionsFor() {
            return (classes) => [
                { text: 'All labels', value: undefined },
                ...engineOptions(classes),
            ];
        },
    },
    actions: {
        /** Loads the engine's labelled screens for the days a page shows (and
         * a day either side, since the client's clock may not be this
         * browser's) for the current client. Quiet on failure: the pages then
         * fall back to their own categories and say so. */
        async load(shown, alsoFor = []) {
            // `alsoFor`: days whose labels are wanted but which aren't shown (the
            // previous period, which the page compares with); they never decide
            // whether the page uses the engine's labels.
            const around = (list) => list.length ? [shiftDate(list[0], -1), ...list, shiftDate(list[list.length - 1], 1)] : [];
            // The days shown first, so the page can draw; the previous period's
            // after, for its comparison only.
            const days = Array.from(new Set(around(shown))).sort();
            const extra = Array.from(new Set(around(alsoFor)))
                .filter(d => !days.includes(d))
                .sort();
            const hub = useHubStore();
            const unlock = useOrgKeyUnlockStore();
            const orgId = hub.defaultOrganization?.id ?? null;
            if (!orgId) {
                this.forget();
                return;
            }
            // Asked again for the same days (unlocked again, the page opened
            // again): the previous period's labels held stay until fresh ones come.
            const same = this.orgId === orgId && this.shown.join() === shown.join();
            const kept = same && this.byDay
                ? Object.fromEntries(Object.entries(this.byDay).filter(([d]) => extra.includes(d)))
                : {};
            const keptAlso = same ? this.alsoDays.filter(d => alsoFor.includes(d)) : [];
            const keptIds = same
                ? Object.fromEntries(Object.entries(this.idsByDay).filter(([d]) => extra.includes(d)))
                : {};
            const keptStaff = same
                ? Object.fromEntries(Object.entries(this.staffByDay).filter(([d]) => extra.includes(d)))
                : {};
            // A new load says nothing yet about the previous period's labels.
            this.alsoError = null;
            if (this.orgId !== orgId)
                this.forget();
            // Labels for other days than those shown now are dropped at once, not
            // when the new ones arrive: until then the page uses its own
            // categories, rather than reading the new days as not looked at.
            else if (this.shown.join() !== shown.join()) {
                this.byDay = null;
                this.days = [];
                this.alsoDays = [];
                this.withoutScreens = [];
                this.failed = 0;
                this.failedDays = [];
            }
            this.orgId = orgId;
            this.shown = shown;
            const request = ++this.request;
            // Taken before anything is awaited: a lock, a sign-out or a change of
            // key from here on (even while the engine's account is looked up)
            // ends this load, and nothing it opens is kept or shown.
            const since = openedGeneration(orgId);
            const secret = unlock.keys[orgId] ?? null;
            const current = () => this.request === request &&
                this.orgId === orgId &&
                openedGeneration(orgId) === since &&
                (unlock.keys[orgId] ?? null) === secret;
            this.loading = true;
            try {
                const reader = await readerFor(orgId, secret, hub.session?.user_id);
                if (!current())
                    return;
                this.ready = !!reader;
                if (!reader) {
                    this.byDay = null;
                    this.days = [];
                    this.alsoDays = [];
                    this.withoutScreens = [];
                    this.failed = 0;
                    this.error = null;
                    return;
                }
                const loaded = await loadEngineScreens(reader, days, since);
                if (!current())
                    return;
                const shownSet = new Set(shown);
                const alsoSet = new Set(alsoFor);
                this.byDay = { ...kept, ...loaded.byDay };
                this.staffByDay = { ...keptStaff, ...loaded.staffByDay };
                this.idsByDay = { ...keptIds, ...(loaded.idsByDay ?? {}) };
                this.days = loaded.days.filter(d => shownSet.has(d));
                this.withoutScreens = (loaded.withoutScreens ?? []).filter(d => shownSet.has(d));
                this.alsoDays = Array.from(new Set([...keptAlso, ...loaded.days.filter(d => alsoSet.has(d))])).sort();
                this.clock = loaded.clock;
                // Only days the page shows: a slack day that won't open is no concern of this page.
                this.failed = loaded.failedDays.filter(d => shownSet.has(d)).length;
                this.failedDays = loaded.failedDays;
                this.error = null;
                // The page can draw now; the previous period's labels follow.
                this.loading = false;
                if (!extra.length)
                    return;
                try {
                    const before = await loadEngineScreens(reader, extra, since);
                    if (!current())
                        return;
                    // The previous period's days and the shown ones never overlap, so
                    // what just came in replaces only what was kept from before.
                    this.byDay = { ...(this.byDay ?? {}), ...before.byDay };
                    this.staffByDay = { ...this.staffByDay, ...before.staffByDay };
                    this.idsByDay = { ...this.idsByDay, ...(before.idsByDay ?? {}) };
                    this.alsoDays = Array.from(new Set([...this.alsoDays, ...before.days.filter(d => alsoSet.has(d))])).sort();
                    this.failedDays = Array.from(new Set([...this.failedDays, ...before.failedDays])).sort();
                    this.alsoError = null;
                }
                catch (err) {
                    // The comparison stays off (its days aren't all labelled), and the
                    // page says why; the period shown is unaffected.
                    if (!current())
                        return;
                    this.alsoDays = [];
                    this.alsoError = err instanceof Error ? err.message : String(err);
                }
            }
            catch (err) {
                if (!current())
                    return;
                this.byDay = null;
                this.days = [];
                this.alsoDays = [];
                this.withoutScreens = [];
                this.failed = 0;
                // Forgotten while loading (a key changed under it) is no Hub fault.
                this.error =
                    err?.name === 'ForgottenWhileLoading'
                        ? null
                        : err instanceof Error
                            ? err.message
                            : String(err);
            }
            finally {
                // The load is over whatever ended it; a newer load sets its own.
                if (this.request === request)
                    this.loading = false;
            }
        },
        /** Drops everything held, opened results included, and makes any load
         * in flight come to nothing. */
        forget() {
            this.request += 1;
            if (this.orgId)
                forgetOpened(this.orgId);
            this.orgId = null;
            this.byDay = null;
            this.staffByDay = {};
            this.idsByDay = {};
            this.shown = [];
            this.days = [];
            this.alsoDays = [];
            this.withoutScreens = [];
            this.failed = 0;
            this.failedDays = [];
            this.error = null;
            this.alsoError = null;
            this.clock = DEFAULT_CLOCK;
            this.ready = false;
            this.loading = false;
        },
    },
});
